import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "../i18n";
import { Media } from "../ui/Media";

/**
 * Interactive "More than just supplements" carousel.
 * Click a circle → it becomes the big active one, siblings shrink & shift.
 * Replace `src` of each item with real photos from /assets later.
 */

export function Problem() {
  const { t } = useI18n();
  const [active, setActive] = useState(1);

  const items = [
    {
      caption: t("problem.c1"),
      detail: "Most supplement advice ignores your bloodwork entirely. Vitamin D deficiency? Iron overload? Doesn't matter — you get the same multivitamin as everyone else.",
      src: undefined, // import problem1 from '../assets/problem-1.jpg'
    },
    {
      caption: t("problem.c2"),
      detail: "Endless forums, conflicting studies, sponsored reviews. By the time you've found a protocol you trust, the evidence has moved on.",
      src: undefined,
    },
    {
      caption: t("problem.c3"),
      detail: "Calcium blocks iron. Zinc competes with copper. Timing matters. A pile of bottles on the counter isn't a plan — it's noise.",
      src: undefined,
    },
  ];

  return (
    <section className="relative py-16 md:py-28 overflow-hidden">
      <div className="max-w-3xl mx-auto text-center px-6">
        <h2 className="text-3xl sm:text-4xl md:text-5xl leading-[1.1]">{t("problem.title")}</h2>
        <p className="mt-4 text-[var(--color-muted)] text-sm sm:text-base">{t("problem.sub")}</p>
      </div>

      {/* Circle row — centered on desktop, horizontally scrollable on mobile */}
      <div className="mt-10 md:mt-16 relative">
        <div className="flex items-center justify-center gap-4 sm:gap-6 overflow-x-auto md:overflow-visible px-6 md:px-0 py-6 md:py-8 scrollbar-hide snap-x snap-mandatory">
          {items.map((it, i) => {
            const isActive = active === i;
            const isNeighbor = Math.abs(active - i) === 1;
            // Responsive sizes via clamp: min / preferred(vw) / max
            const size = isActive
              ? "clamp(200px, 32vw, 460px)"
              : isNeighbor
              ? "clamp(140px, 22vw, 320px)"
              : "clamp(100px, 16vw, 240px)";
            return (
              <motion.button
                key={i}
                onClick={() => setActive(i)}
                layout
                initial={false}
                animate={{ opacity: isActive ? 1 : 0.75 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                style={{ width: size, height: size }}
                className="shrink-0 relative rounded-full overflow-hidden snap-center cursor-pointer outline-none focus-visible:ring-2 ring-[var(--color-moss)] transition-[width,height] duration-500"
              >
                <Media src={it.src} label={`image ${i + 1}`} rounded="rounded-full" />
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 ring-4 ring-[var(--color-forest-deep)]/30 rounded-full pointer-events-none"
                    />
                  )}
                </AnimatePresence>
              </motion.button>
            );
          })}
        </div>

        {/* Captions under active + neighbours */}
        <div className="max-w-6xl mx-auto grid grid-cols-3 gap-4 md:gap-8 text-center mt-4 md:mt-6 px-6">
          {[active - 1, active, active + 1].map((idx, slot) => {
            const it = items[idx];
            if (!it) return <div key={`slot-${slot}`} />;
            const isCenter = slot === 1;
            return (
              <p
                key={`cap-${idx}`}
                className={`transition-opacity ${
                  isCenter
                    ? "text-[var(--color-ink)] text-base sm:text-lg md:text-xl"
                    : "text-[var(--color-muted)] text-xs sm:text-sm opacity-60"
                }`}
              >
                {it.caption}
              </p>
            );
          })}
        </div>

        {/* Detail panel for active */}
        <div className="max-w-2xl mx-auto mt-8 md:mt-10 px-6 min-h-[90px]">
          <AnimatePresence mode="wait">
            <motion.p
              key={active}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
              className="text-center text-[var(--color-muted)] leading-relaxed text-sm sm:text-base"
            >
              {items[active]?.detail}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Dots */}
        <div className="mt-6 md:mt-8 flex justify-center gap-2">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === active ? "w-8 bg-[var(--color-forest-deep)]" : "w-2 bg-[var(--color-forest-deep)]/25"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
